import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { chatApi, supportApi, notificationsApi } from '../lib/api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

// Simple notification sound (short beep via Web Audio API)
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch {
    // Audio not available
  }
}

export function NotificationProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [notifUnreadCount, setNotifUnreadCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const prevCountRef = useRef(0);
  const prevSupportCountRef = useRef(0);
  const prevNotifCountRef = useRef(0);
  const intervalRef = useRef(null);

  // Check notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) return 'denied';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    
    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);
    return permission;
  }, []);

  const showBrowserNotification = useCallback((title, body) => {
    if (notificationPermission !== 'granted') return;
    if (document.hasFocus()) return;
    
    try {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.svg',
        badge: '/favicon.svg',
        tag: 'habeshatown-chat',
        renotify: true,
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch {
      // Notification failed
    }
  }, [notificationPermission]);

  // Poll unread counts (chat + support)
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const { data } = await chatApi.getUnreadCount();
      const newCount = data.count || 0;
      
      if (newCount > prevCountRef.current && prevCountRef.current >= 0) {
        playNotificationSound();
        showBrowserNotification(
          'New message on HabeshaTown',
          'You have a new unread message'
        );
      }
      
      prevCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch {
      // silently fail
    }

    // Also fetch support unread for admin
    try {
      const { data } = await supportApi.getUnreadCount();
      const newSupportCount = data.count || 0;
      
      if (newSupportCount > prevSupportCountRef.current && prevSupportCountRef.current >= 0) {
        if (newSupportCount > 0) {
          playNotificationSound();
          showBrowserNotification(
            'New support ticket',
            'A user needs help on HabeshaTown'
          );
        }
        setIsAdmin(true);
      }
      
      prevSupportCountRef.current = newSupportCount;
      setSupportUnreadCount(newSupportCount);
    } catch {
      // silently fail - not admin or endpoint unavailable
    }

    // Fetch notification unread count
    try {
      const { data } = await notificationsApi.getUnreadCount();
      const newNotifCount = data.count || 0;
      
      if (newNotifCount > prevNotifCountRef.current && prevNotifCountRef.current >= 0) {
        playNotificationSound();
        showBrowserNotification(
          'New notification',
          'You have a new notification on HabeshaTown'
        );
      }
      
      prevNotifCountRef.current = newNotifCount;
      setNotifUnreadCount(newNotifCount);
    } catch {
      // silently fail
    }
  }, [isAuthenticated, showBrowserNotification]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      setSupportUnreadCount(0);
      setNotifUnreadCount(0);
      setIsAdmin(false);
      prevCountRef.current = -1;
      prevSupportCountRef.current = -1;
      prevNotifCountRef.current = -1;
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    // Initial fetch (don't trigger notification on first load)
    prevCountRef.current = -1;
    prevSupportCountRef.current = -1;
    prevNotifCountRef.current = -1;
    
    Promise.all([
      chatApi.getUnreadCount(),
      supportApi.getUnreadCount(),
      supportApi.checkAdmin(),
      notificationsApi.getUnreadCount()
    ]).then(([chatRes, supportRes, adminRes, notifRes]) => {
      const count = chatRes.data.count || 0;
      setUnreadCount(count);
      prevCountRef.current = count;
      
      const sCount = supportRes.data.count || 0;
      setSupportUnreadCount(sCount);
      prevSupportCountRef.current = sCount;
      
      setIsAdmin(adminRes.data.is_admin || false);

      const nCount = notifRes.data.count || 0;
      setNotifUnreadCount(nCount);
      prevNotifCountRef.current = nCount;
    }).catch(() => {});

    // Poll every 5 seconds
    intervalRef.current = setInterval(fetchUnreadCount, 5000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAuthenticated, fetchUnreadCount]);

  // Update unread count when marking a conversation as read
  const markConversationRead = useCallback(async (conversationId) => {
    try {
      await chatApi.markRead(conversationId);
      const { data } = await chatApi.getUnreadCount();
      const newCount = data.count || 0;
      setUnreadCount(newCount);
      prevCountRef.current = newCount;
    } catch {
      // silently fail
    }
  }, []);

  // Refresh support count (e.g. after replying to a ticket)
  const refreshSupportCount = useCallback(async () => {
    try {
      const { data } = await supportApi.getUnreadCount();
      const newCount = data.count || 0;
      setSupportUnreadCount(newCount);
      prevSupportCountRef.current = newCount;
    } catch {}
  }, []);

  // Refresh notification count
  const refreshNotifCount = useCallback(async () => {
    try {
      const { data } = await notificationsApi.getUnreadCount();
      const newCount = data.count || 0;
      setNotifUnreadCount(newCount);
      prevNotifCountRef.current = newCount;
    } catch {}
  }, []);

  return (
    <NotificationContext.Provider value={{
      unreadCount,
      supportUnreadCount,
      notifUnreadCount,
      isAdmin,
      notificationPermission,
      requestNotificationPermission,
      markConversationRead,
      refreshUnreadCount: fetchUnreadCount,
      refreshSupportCount,
      refreshNotifCount,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
